import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { neighborBusinessService, getStrapiMediaUrl } from '../services/api';
import { Link as RouterLink } from 'react-router-dom';

// Category display names and colors
const categoryConfig = {
  home_lawn_services: { label: 'Home & Lawn Services', color: 'success' },
  construction_renovation: { label: 'Construction & Renovation', color: 'warning' },
  professional_legal: { label: 'Professional & Legal', color: 'info' },
  financial_insurance: { label: 'Financial & Insurance', color: 'primary' },
  retail: { label: 'Retail', color: 'secondary' },
  food_beverage: { label: 'Food & Beverage', color: 'error' },
  health_beauty_fitness: { label: 'Health, Beauty & Fitness', color: 'success' },
  automotive: { label: 'Automotive', color: 'warning' },
  childcare_education: { label: 'Childcare & Education', color: 'info' },
  pet_services: { label: 'Pet Services', color: 'secondary' },
  technology: { label: 'Technology', color: 'primary' },
  creative_events: { label: 'Creative & Events', color: 'error' },
  other: { label: 'Other', color: 'default' }
};

const NeighborBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        const response = await neighborBusinessService.getAll();
        
        if (response && response.data) {
          setBusinesses(response.data);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching businesses:', err);
        setError('Failed to load businesses. Please try again later.');
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, []);

  // Group businesses by category
  const groupedBusinesses = businesses.reduce((acc, business) => {
    const category = business.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(business);
    return acc;
  }, {});

  const getCategoryInfo = (category) => {
    return categoryConfig[category] || categoryConfig.other;
  };

  return (
    <>
      <Helmet>
        <title>Neighbor Businesses Directory | Brynleigh Estates HOA</title>
        <meta name="description" content="Support local businesses owned by your Brynleigh Estates neighbors. Browse our community business directory featuring services from residents." />
        <link rel="canonical" href="https://brynleighestates.com/neighbor-businesses" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/neighbor-businesses" />
        <meta property="og:title" content="Neighbor Businesses Directory | Brynleigh Estates HOA" />
        <meta property="og:description" content="Support local businesses owned by your Brynleigh Estates neighbors. Browse our community business directory." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Neighbor Businesses Directory | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Support local businesses owned by your Brynleigh Estates neighbors." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        title="Neighbor Businesses" 
        subtitle="Support local businesses owned by your neighbors"
        imageUrl="/images/hills-bg.jpeg"
      />
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom align="center">
            Local Business Directory
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2, maxWidth: 700, mx: 'auto' }}>
            Our community is home to many talented professionals and business owners. 
            Browse the directory below to discover services offered by your neighbors 
            and support local entrepreneurs.
          </Typography>
          <Divider sx={{ mb: 4 }} />
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>
          ) : businesses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Businesses Listed Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back soon! We're building our directory of neighbor-owned businesses.
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupedBusinesses).map(([category, categoryBusinesses]) => {
              const categoryInfo = getCategoryInfo(category);
              
              return (
                <Box key={category} sx={{ mb: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Chip 
                      label={categoryInfo.label} 
                      color={categoryInfo.color}
                      sx={{ fontWeight: 600 }}
                    />
                    <Divider sx={{ flexGrow: 1, ml: 2 }} />
                  </Box>
                  
                  <Grid container spacing={3}>
                    {categoryBusinesses.map((business, index) => (
                      <Grid item xs={12} sm={6} md={4} key={business.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <Card 
                            elevation={2}
                            sx={{ 
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: 2,
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4
                              }
                            }}
                          >
                            {business.logo && (
                              <CardMedia
                                component="img"
                                height="140"
                                image={getStrapiMediaUrl(business.logo?.url)}
                                alt={`${business.businessName} logo`}
                                sx={{ objectFit: 'contain', p: 2, bgcolor: 'grey.50' }}
                              />
                            )}
                            
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" component="h3" gutterBottom>
                                {business.businessName}
                              </Typography>
                              
                              {business.ownerName && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <PersonIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {business.ownerName}
                                  </Typography>
                                </Box>
                              )}
                              
                              {business.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {business.description.length > 120 
                                    ? (
                                      <>
                                        {business.description.substring(0, 120)}...{' '}
                                        <Link 
                                          component="button" 
                                          variant="body2" 
                                          onClick={() => setSelectedBusiness(business)}
                                          sx={{ verticalAlign: 'baseline' }}
                                        >
                                          Read more
                                        </Link>
                                      </>
                                    )
                                    : business.description}
                                </Typography>
                              )}
                              
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {business.phone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PhoneIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body2">
                                      <a href={`tel:${business.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                        {business.phone}
                                      </a>
                                    </Typography>
                                  </Box>
                                )}
                                
                                {business.email && (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <EmailIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      overflow: 'hidden', 
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      <a href={`mailto:${business.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                        {business.email}
                                      </a>
                                    </Typography>
                                  </Box>
                                )}
                                
                                {business.address && (
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1, mt: 0.3 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {business.address}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </CardContent>
                            
                            {business.website && (
                              <CardActions sx={{ p: 2, pt: 0 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<LanguageIcon />}
                                  href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  fullWidth
                                >
                                  Visit Website
                                </Button>
                              </CardActions>
                            )}
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })
          )}
        </Box>
        
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.50' }}>
          <Typography variant="body1" gutterBottom>
            Own a business and want to be listed here?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submit your business to be featured in our community directory.
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/submit-business"
            startIcon={<AddBusinessIcon />}
          >
            Submit Your Business
          </Button>
        </Paper>
      </Container>

      {/* Business Details Dialog */}
      <Dialog 
        open={Boolean(selectedBusiness)} 
        onClose={() => setSelectedBusiness(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedBusiness && (
          <>
            <DialogTitle>
              {selectedBusiness.businessName}
              {selectedBusiness.ownerName && (
                <Typography variant="body2" color="text.secondary">
                  {selectedBusiness.ownerName}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent dividers>
              {selectedBusiness.logo && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img 
                    src={getStrapiMediaUrl(selectedBusiness.logo?.url)} 
                    alt={`${selectedBusiness.businessName} logo`}
                    style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}
              
              {selectedBusiness.description && (
                <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedBusiness.description}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {selectedBusiness.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <a href={`tel:${selectedBusiness.phone}`} style={{ color: 'inherit' }}>
                        {selectedBusiness.phone}
                      </a>
                    </Typography>
                  </Box>
                )}
                
                {selectedBusiness.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <a href={`mailto:${selectedBusiness.email}`} style={{ color: 'inherit' }}>
                        {selectedBusiness.email}
                      </a>
                    </Typography>
                  </Box>
                )}
                
                {selectedBusiness.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1, mt: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedBusiness.address}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              {selectedBusiness.website && (
                <Button
                  startIcon={<LanguageIcon />}
                  href={selectedBusiness.website.startsWith('http') ? selectedBusiness.website : `https://${selectedBusiness.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website
                </Button>
              )}
              <Button onClick={() => setSelectedBusiness(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default NeighborBusinesses;
